import React, { useState, useEffect } from 'react';
import { 
  Search, 
  UserCheck, 
  UserX, 
  RefreshCw, 
  AlertCircle,
  Eye,
  Phone,
  Mail,
  User,
  Star,
  Edit,
  ArrowLeft,
  Car,
  FileText,
  Clock,
  Wallet
} from 'lucide-react';
import { isValidEmail, isValidFrenchMobile, normalizeFrenchMobile } from '../utils/validation';

export default function DriverProfile() {
  const [search, setSearch] = useState('');
  const [drivers, setDrivers] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  
  // Detail states
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedDriverDetails, setSelectedDriverDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [documentModal, setDocumentModal] = useState({ open: false, url: null, type: null, loading: false });
  
  // Editing states
  const [isEditing, setIsEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editBalance, setEditBalance] = useState('');
  const [editVehicleBrand, setEditVehicleBrand] = useState('');
  const [editVehicleModel, setEditVehicleModel] = useState('');
  const [editLicensePlate, setEditLicensePlate] = useState('');
  const [editRevenueMode, setEditRevenueMode] = useState('COMMISSION');
  const [editError, setEditError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const token = localStorage.getItem('adminToken');

  const fetchData = async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    else setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/admin/users/drivers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement des chauffeurs');
      const data = await response.json();
      setDrivers(data);
    } catch (err) {
      console.error(err);
      setError('Impossible de charger la liste des chauffeurs. Vérifiez votre connexion.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openDocument = async (fileName, type) => {
    setDocumentModal({ open: true, url: null, type, loading: true });
    
    try {
      const response = await fetch(`/api/admin/files/${fileName}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Impossible de charger le document');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      setDocumentModal({ open: true, url, type, loading: false });
    } catch (err) {
      alert(`Erreur: ${err.message}`);
      setDocumentModal({ open: false, url: null, type: null, loading: false });
    }
  };

  // Filter drivers
  const getFilteredData = () => {
    const term = search.toLowerCase().trim();
    return drivers.filter(d => 
      (d.firstName && d.firstName.toLowerCase().includes(term)) ||
      (d.lastName && d.lastName.toLowerCase().includes(term)) ||
      (d.email && d.email.toLowerCase().includes(term)) ||
      (d.phone && d.phone.includes(term)) ||
      (d.vehicleBrand && d.vehicleBrand.toLowerCase().includes(term)) ||
      (d.vehicleModel && d.vehicleModel.toLowerCase().includes(term)) ||
      (d.licensePlate && d.licensePlate.toLowerCase().includes(term))
    );
  };

  // Driver suspension
  const handleToggleDriverSuspension = async (driver) => {
    const isSuspended = driver.suspended;
    const action = isSuspended ? 'reactivate' : 'suspend';
    const confirmMsg = isSuspended 
      ? `Voulez-vous réactiver le compte du chauffeur ${driver.firstName} ${driver.lastName} ?`
      : `Voulez-vous suspendre le compte du chauffeur ${driver.firstName} ${driver.lastName} ? Ce chauffeur ne recevra plus de courses.`;
    
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`/api/admin/drivers/${driver.id}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      
      setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, suspended: !isSuspended } : d));
      if (selectedDriver && selectedDriver.id === driver.id) {
        setSelectedDriver(prev => ({ ...prev, suspended: !isSuspended }));
        if (selectedDriverDetails?.driver) {
          setSelectedDriverDetails(prev => ({
            ...prev,
            driver: { ...prev.driver, suspended: !isSuspended }
          }));
        }
      }
    } catch (err) {
      alert('Erreur lors du changement de statut du chauffeur.');
    }
  };

  const handleDriverSelect = async (driver) => {
    setSelectedDriver(driver);
    setSelectedDriverDetails(null);
    setIsEditing(false);
    setDetailsLoading(true);
    try {
      const res = await fetch(`/api/admin/drivers/${driver.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSelectedDriverDetails(data);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const startEditing = () => {
    setEditFirstName(selectedDriver.firstName || '');
    setEditLastName(selectedDriver.lastName || '');
    setEditEmail(selectedDriver.email || '');
    setEditPhone(selectedDriver.phone || '');
    setEditBalance(selectedDriver.balance !== undefined ? selectedDriver.balance : 0);
    setEditVehicleBrand(selectedDriver.vehicleBrand || '');
    setEditVehicleModel(selectedDriver.vehicleModel || '');
    setEditLicensePlate(selectedDriver.licensePlate || '');
    setEditRevenueMode(selectedDriver.revenueMode || 'COMMISSION');
    setEditError('');
    setIsEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editFirstName.trim() || !editLastName.trim()) {
      setEditError('Le prénom et le nom sont obligatoires.');
      return;
    }
    if (!isValidEmail(editEmail)) {
      setEditError('Format de l\'email invalide.');
      return;
    }
    if (!editPhone.trim()) {
      setEditError('Le numéro de téléphone est obligatoire.');
      return;
    }
    if (!isValidFrenchMobile(editPhone)) {
      setEditError('Numéro de téléphone mobile français invalide (06 ou 07 uniquement).');
      return;
    }

    setSavingEdit(true);

    const normalizedPhone = normalizeFrenchMobile(editPhone);

    const body = {
      firstName: editFirstName.trim(),
      lastName: editLastName.trim(),
      email: editEmail.trim(),
      phone: normalizedPhone,
      balance: parseFloat(editBalance) || 0,
      vehicleBrand: editVehicleBrand.trim(),
      vehicleModel: editVehicleModel.trim(),
      licensePlate: editLicensePlate.trim().toUpperCase(),
      revenueMode: editRevenueMode
    };

    try {
      const res = await fetch(`/api/admin/users/drivers/${selectedDriver.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Erreur lors de la mise à jour.');
      }

      setDrivers(prev => prev.map(d => d.id === selectedDriver.id ? data : d));
      setSelectedDriver(data);
      setSelectedDriverDetails(prev => prev ? { ...prev, driver: data } : null);
      setIsEditing(false);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const renderDriverDetailPage = () => {
    if (!selectedDriver) return null;
    
    const fullName = `${selectedDriver.firstName || ''} ${selectedDriver.lastName || ''}`.trim() || 'Sans Nom';
    const isSuspended = selectedDriver.suspended;
    
    return (
      <div className="driver-page-container">
        {/* Back navigation & Header */}
        <div className="driver-detail-header">
          <button 
            onClick={() => setSelectedDriver(null)} 
            className="btn-secondary driver-back-btn"
          >
            <ArrowLeft size={16} />
            <span>Retour à la liste des chauffeurs</span>
          </button>
          
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
            Fiche Chauffeur 360° : {fullName}
          </h2>
        </div>

        {isEditing ? (
          <div className="glass-card driver-edit-card">
            <div style={styles.editCardHeader}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Modifier les informations de {fullName}</h3>
            </div>
            <form onSubmit={handleSaveEdit} style={styles.editForm}>
              {editError && (
                <div style={styles.formAlertError}>
                  <AlertCircle size={16} />
                  <span>{editError}</span>
                </div>
              )}

              <div className="driver-form-grid">
                <div style={styles.formGroup}>
                  <label style={styles.label}>Prénom *</label>
                  <input 
                    type="text" 
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Nom de famille *</label>
                  <input 
                    type="text" 
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Adresse Email *</label>
                  <input 
                    type="email" 
                    value={editEmail}
                    onChange={(e) => setEditEmail(e.target.value)}
                    required
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Téléphone * (06/07)</label>
                  <input 
                    type="text" 
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    required
                    placeholder="0612345678"
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Solde Portefeuille (€)</label>
                  <input 
                    type="number" 
                    step="0.01"
                    value={editBalance}
                    onChange={(e) => setEditBalance(e.target.value)}
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Marque du véhicule</label>
                  <input 
                    type="text" 
                    value={editVehicleBrand}
                    onChange={(e) => setEditVehicleBrand(e.target.value)}
                    placeholder="Peugeot, Renault, Mercedes..."
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Modèle du véhicule</label>
                  <input 
                    type="text" 
                    value={editVehicleModel}
                    onChange={(e) => setEditVehicleModel(e.target.value)}
                    placeholder="508, Megane, Classe C..."
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Plaque d'immatriculation</label>
                  <input 
                    type="text" 
                    value={editLicensePlate}
                    onChange={(e) => setEditLicensePlate(e.target.value.toUpperCase())}
                    placeholder="AA-123-BB"
                    style={styles.formInput}
                    className="input-field"
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Mode de Rémunération</label>
                  <select 
                    value={editRevenueMode} 
                    onChange={(e) => setEditRevenueMode(e.target.value)}
                    style={styles.formInput}
                    className="input-field"
                  >
                    <option value="COMMISSION">Commission (par course)</option>
                    <option value="SUBSCRIPTION">Abonnement fixe</option>
                  </select>
                </div>
              </div>

              <div className="driver-edit-actions">
                <button 
                  type="button" 
                  onClick={() => setIsEditing(false)} 
                  className="btn-secondary"
                  style={{ padding: '10px 20px' }}
                >
                  Annuler
                </button>
                <button 
                  type="submit" 
                  disabled={savingEdit}
                  className="btn-primary"
                  style={{ padding: '10px 20px' }}
                >
                  {savingEdit ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <div className="driver-detail-grid">
            
            {/* Left Column: Driver Profile Summary */}
            <div className="driver-detail-left-col">
              <div style={styles.profileSummaryCard} className="glass-card">
                <div style={styles.profileSummaryHeader}>
                  <div style={{
                    ...styles.detailLargeAvatar,
                    backgroundColor: isSuspended ? 'var(--danger-bg)' : 'rgba(0, 82, 255, 0.08)',
                    borderColor: isSuspended ? 'var(--danger-border)' : 'rgba(0, 82, 255, 0.15)'
                  }}>
                    {selectedDriver.firstName ? selectedDriver.firstName[0].toUpperCase() : <User size={32} />}
                  </div>
                  <h3 style={styles.detailProfileName}>{fullName}</h3>
                  <p style={styles.detailProfileRole}>Chauffeur Partenaire</p>
                  
                  <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {isSuspended ? (
                      <span className="status-badge danger">Compte Suspendu</span>
                    ) : (
                      <span className="status-badge success">Compte Actif</span>
                    )}
                    {selectedDriver.validationStatus === 'VALIDATED' ? (
                      <span className="status-badge success">Documents Validés</span>
                    ) : (
                      <span className="status-badge warning">Validation en attente</span>
                    )}
                  </div>
                </div>

                <div style={styles.profileDetailsList}>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>ID Chauffeur :</span>
                    <span style={styles.detailItemValMono}>{selectedDriver.id}</span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Date d'inscription :</span>
                    <span style={styles.detailItemVal}>
                      {selectedDriver.createdAt ? new Date(selectedDriver.createdAt).toLocaleDateString('fr-FR') : 'Non renseignée'}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Statut Connexion :</span>
                    <span style={styles.detailItemVal}>
                      {selectedDriver.online ? (
                        <span className="status-badge success" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'var(--success)', display: 'inline-block' }} />
                          En ligne
                        </span>
                      ) : (
                        <span className="status-badge secondary">Hors ligne</span>
                      )}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Dernière connexion :</span>
                    <span style={styles.detailItemVal}>
                      {selectedDriver.lastConnectionDate ? new Date(selectedDriver.lastConnectionDate).toLocaleString('fr-FR') : 'Jamais connecté'}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Email :</span>
                    <span style={styles.detailItemVal}>{selectedDriver.email}</span>
                  </div>
                  {selectedDriver.phone && (
                    <div style={styles.detailItemRow}>
                      <span style={styles.detailItemKey}>Téléphone :</span>
                      <span style={styles.detailItemVal}>{selectedDriver.phone}</span>
                    </div>
                  )}
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Solde Portefeuille :</span>
                    <span style={{ ...styles.detailItemVal, color: 'var(--success)', fontWeight: 'bold' }}>
                      {(selectedDriver.balance !== undefined ? selectedDriver.balance : 0).toFixed(2)} €
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Note Chauffeur :</span>
                    <span style={styles.detailItemVal}>
                      <Star size={12} fill="#eab308" color="#eab308" style={{ marginRight: '4px' }} /> 
                      {selectedDriver.rating ? selectedDriver.rating.toFixed(1) : '5.0'} / 5.0
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Mode de Rémunération :</span>
                    <span style={styles.detailItemVal}>
                      <span className={`status-badge ${selectedDriver.revenueMode === 'SUBSCRIPTION' ? 'info' : 'secondary'}`}>
                        {selectedDriver.revenueMode === 'SUBSCRIPTION' ? 'Abonnement' : 'Commission'}
                      </span>
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Véhicule :</span>
                    <span style={styles.detailItemVal}>
                      {selectedDriver.vehicleBrand || selectedDriver.vehicleModel 
                        ? `${selectedDriver.vehicleBrand || ''} ${selectedDriver.vehicleModel || ''}`.trim() 
                        : 'Non spécifié'}
                    </span>
                  </div>
                  <div style={styles.detailItemRow}>
                    <span style={styles.detailItemKey}>Immatriculation :</span>
                    <span style={{ ...styles.detailItemValMono, textTransform: 'uppercase' }}>
                      {selectedDriver.licensePlate || 'N/A'}
                    </span>
                  </div>
                </div>

                <div style={styles.profileActionBlock}>
                  <h4 style={styles.actionBlockTitle}>Actions de Gestion</h4>
                  <div style={styles.actionButtonsGrid}>
                    <button 
                      onClick={startEditing} 
                      style={styles.managerActionBtnPrimary}
                    >
                      <Edit size={14} />
                      <span>Modifier le Profil</span>
                    </button>
                    <button 
                      onClick={() => handleToggleDriverSuspension(selectedDriver)} 
                      style={{ 
                        ...styles.managerActionBtnDanger,
                        backgroundColor: isSuspended ? 'var(--success-bg)' : 'var(--danger-bg)',
                        color: isSuspended ? 'var(--success)' : 'var(--danger)',
                        borderColor: isSuspended ? 'var(--success-border)' : 'var(--danger-border)'
                      }}
                    >
                      {isSuspended ? <UserCheck size={14} /> : <UserX size={14} />}
                      <span>{isSuspended ? 'Réactiver le Compte' : 'Suspendre le Compte'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Documents, Rides & Transactions */}
            <div className="driver-detail-right-col">
              {detailsLoading ? (
                <div style={styles.detailTabLoadingContainer} className="glass-card">
                  <RefreshCw size={24} className="spin-anim" color="var(--primary)" />
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>Chargement des données du chauffeur...</p>
                </div>
              ) : selectedDriverDetails ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  
                  {/* Documents Section */}
                  <div style={styles.detailSectionCard} className="glass-card">
                    <div style={styles.detailSectionHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileText size={16} color="var(--warning)" />
                        <h4 style={styles.detailSectionTitle}>
                          Pièces Justificatives ({selectedDriverDetails.documents?.length || 0})
                        </h4>
                      </div>
                    </div>
                    <div style={styles.detailSectionBody}>
                      {selectedDriverDetails.documents && selectedDriverDetails.documents.length > 0 ? (
                        <div style={styles.docList}>
                          {selectedDriverDetails.documents.map((doc) => {
                            const fileName = doc.url ? doc.url.split('/').pop() : null;
                            return (
                              <div key={doc.id} style={styles.docItem}>
                                <div style={styles.docMeta}>
                                  <span style={styles.docName}>{doc.type}</span>
                                  {fileName ? (
                                    <button 
                                      onClick={() => openDocument(fileName, doc.type)}
                                      style={styles.docLinkButton}
                                    >
                                      Voir le document
                                    </button>
                                  ) : (
                                    <span style={styles.docUrl}>Aucun fichier</span>
                                  )}
                                </div>
                                <span style={{
                                  ...styles.statusBadgeSmall,
                                  color: doc.status === 'VALIDATED' ? 'var(--success)' : doc.status === 'REJECTED' ? 'var(--danger)' : 'var(--warning)',
                                  backgroundColor: doc.status === 'VALIDATED' ? 'var(--success-bg)' : doc.status === 'REJECTED' ? 'var(--danger-bg)' : 'var(--warning-bg)',
                                }}>
                                  {doc.status}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div style={styles.emptyDetailSection}>Aucun document soumis pour le moment.</div>
                      )}
                    </div>
                  </div>

                  {/* Rides History Section */}
                  <div style={styles.detailSectionCard} className="glass-card">
                    <div style={styles.detailSectionHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={16} color="var(--success)" />
                        <h4 style={styles.detailSectionTitle}>
                          Historique des Courses ({selectedDriverDetails.rides?.length || 0})
                        </h4>
                      </div>
                    </div>
                    <div style={styles.detailSectionBody}>
                      {selectedDriverDetails.rides && selectedDriverDetails.rides.length > 0 ? (
                        <div className="driver-table-scroll">
                          <table className="admin-table driver-table-history">
                            <thead>
                              <tr>
                                <th>ID Course</th>
                                <th>Départ</th>
                                <th>Arrivée</th>
                                <th>Date / Heure</th>
                                <th>Tarif Final</th>
                                <th>Statut</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedDriverDetails.rides.map(ride => (
                                <tr key={ride.id}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{ride.id.substring(0, 8)}...</td>
                                  <td>{ride.pickupAddress}</td>
                                  <td>{ride.destinationAddress}</td>
                                  <td>{ride.requestedAt ? new Date(ride.requestedAt).toLocaleString('fr-FR') : 'N/A'}</td>
                                  <td style={{ fontWeight: '600' }}>
                                    {ride.finalPrice ? `${ride.finalPrice.toFixed(2)} €` : 'N/A'}
                                  </td>
                                  <td>
                                    <span className={`status-badge ${
                                      ride.status === 'RIDE_COMPLETED' ? 'success' : 
                                      ride.status === 'CANCELLED' ? 'danger' : 'warning'
                                    }`}>
                                      {ride.status.replace('RIDE_', '')}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={styles.emptyDetailSection}>Aucune course effectuée par ce chauffeur.</div>
                      )}
                    </div>
                  </div>

                  {/* Financial History Section */}
                  <div style={styles.detailSectionCard} className="glass-card">
                    <div style={styles.detailSectionHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Wallet size={16} color="var(--primary)" />
                        <h4 style={styles.detailSectionTitle}>
                          Historique Financier ({selectedDriverDetails.transactions?.length || 0})
                        </h4>
                      </div>
                    </div>
                    <div style={styles.detailSectionBody}>
                      {selectedDriverDetails.transactions && selectedDriverDetails.transactions.length > 0 ? (
                        <div className="driver-table-scroll">
                          <table className="admin-table driver-table-history">
                            <thead>
                              <tr>
                                <th>ID Transaction</th>
                                <th>Type</th>
                                <th>Montant</th>
                                <th>Statut</th>
                                <th>Date / Heure</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedDriverDetails.transactions.map(tx => (
                                <tr key={tx.id}>
                                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{tx.id.substring(0, 8)}...</td>
                                  <td style={{ fontWeight: '500' }}>{tx.type}</td>
                                  <td style={{ 
                                    fontWeight: '600', 
                                    color: tx.amount < 0 || tx.type === 'PAYOUT' ? 'var(--danger)' : 'var(--success)' 
                                  }}>
                                    {tx.amount < 0 || tx.type === 'PAYOUT' ? '-' : '+'}{Math.abs(tx.amount).toFixed(2)} €
                                  </td>
                                  <td>
                                    <span className={`status-badge ${
                                      tx.status === 'COMPLETED' ? 'success' : 
                                      tx.status === 'FAILED' ? 'danger' : 'warning'
                                    }`}>
                                      {tx.status}
                                    </span>
                                  </td>
                                  <td>{tx.createdAt ? new Date(tx.createdAt).toLocaleString('fr-FR') : 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={styles.emptyDetailSection}>Aucune transaction enregistrée.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={styles.detailTabLoadingContainer} className="glass-card">
                  <AlertCircle size={24} color="var(--text-muted)" />
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>Erreur lors du chargement des détails du chauffeur.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Document Modal */}
        {documentModal.open && (
          <div style={styles.modalOverlay} onClick={() => setDocumentModal({ open: false, url: null, type: null, loading: false })}>
            <div className="driver-modal-content" style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600' }}>{documentModal.type}</h3>
                <button onClick={() => setDocumentModal({ open: false, url: null, type: null, loading: false })} style={styles.modalCloseBtn}>
                  ✕
                </button>
              </div>
              <div style={styles.documentFrameContainer}>
                {documentModal.loading ? (
                  <div style={styles.loadingContainer}>Chargement du document...</div>
                ) : documentModal.url ? (
                  <iframe 
                    src={documentModal.url}
                    style={styles.documentFrame}
                    title={documentModal.type}
                  />
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const filteredItems = getFilteredData();

  if (loading) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.spinner}></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Chargement des chauffeurs...</p>
      </div>
    );
  }

  if (selectedDriver) {
    return renderDriverDetailPage();
  }

  return (
    <div className="driver-page-container">
      <style>{`
        .driver-page-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
          animation: fadeIn 0.4s ease;
        }
        .driver-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .driver-detail-header {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }
        .driver-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
        }
        .driver-table-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          width: 100%;
        }
        .driver-admin-table {
          min-width: 680px;
          width: 100%;
        }
        .driver-table-history {
          min-width: 520px;
          width: 100%;
        }
        .driver-detail-grid {
          display: flex;
          gap: 20px;
          flex: 1;
          min-height: 0;
          align-items: flex-start;
        }
        .driver-detail-left-col {
          flex: 0 0 320px;
          max-width: 320px;
          width: 320px;
        }
        .driver-detail-right-col {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 20px;
          overflow-y: auto;
          max-height: calc(100vh - 160px);
          padding-right: 4px;
        }
        .driver-edit-card {
          background-color: var(--bg-secondary);
          padding: 24px;
          max-width: 850px;
          width: 100%;
          margin: 0 auto;
        }
        .driver-form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 16px;
        }
        .driver-edit-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          border-top: 0.5px solid var(--border-color);
          padding-top: 20px;
          margin-top: 12px;
        }
        .driver-modal-content {
          width: 90%;
          max-width: 900px;
          height: 90%;
          max-height: 800px;
        }

        @media (max-width: 768px) {
          .driver-header-row {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          .driver-header-row h2 {
            font-size: 1.15rem !important;
          }
          .driver-header-row button {
            width: 100%;
            justify-content: center;
          }
          .driver-detail-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .driver-back-btn {
            width: 100% !important;
            justify-content: center !important;
          }
          .driver-detail-grid {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .driver-detail-left-col {
            width: 100% !important;
            max-width: 100% !important;
            flex: 1 1 auto !important;
          }
          .driver-detail-right-col {
            max-height: none !important;
            overflow-y: visible !important;
            padding-right: 0 !important;
            width: 100% !important;
          }
          .driver-edit-card {
            padding: 16px !important;
            border-radius: var(--radius-sm) !important;
          }
          .driver-form-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
          }
          .driver-edit-actions {
            flex-direction: column-reverse !important;
          }
          .driver-edit-actions button {
            width: 100% !important;
            justify-content: center !important;
          }
          .driver-modal-content {
            width: 95% !important;
            height: 92% !important;
          }
        }
      `}</style>

      {error && (
        <div style={styles.formAlertError}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Upper header action bar */}
      <div className="driver-header-row">
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
            Gestion des Chauffeurs Partenaires
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {drivers.length} chauffeur{drivers.length > 1 ? 's' : ''} inscrit{drivers.length > 1 ? 's' : ''}
          </p>
        </div>

        <button 
          onClick={() => fetchData(true)} 
          disabled={refreshing} 
          style={styles.refreshBtn}
          className="btn-secondary"
        >
          <RefreshCw size={14} className={refreshing ? 'spin-anim' : ''} />
          <span>{refreshing ? 'Actualisation...' : 'Actualiser la liste'}</span>
        </button>
      </div>

      {/* Search Bar */}
      <div style={styles.filterBar} className="glass-card">
        <Search size={16} style={styles.searchIcon} />
        <input 
          type="text" 
          placeholder="Rechercher un chauffeur (nom, prénom, email, tel, véhicule, plaque)..." 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
          className="input-field"
        />
      </div>

      {/* Table Container */}
      <div style={styles.tablePanel} className="glass-card driver-table-scroll">
        {filteredItems.length === 0 ? (
          <div style={styles.emptyTable}>
            {search ? 'Aucun chauffeur ne correspond à votre recherche.' : 'Aucun chauffeur inscrit.'}
          </div>
        ) : (
          <table className="admin-table driver-admin-table">
            <thead>
              <tr>
                <th>Chauffeur</th>
                <th>Contact</th>
                <th>Véhicule</th>
                <th>Solde / Note</th>
                <th>Mode</th>
                <th>Statut</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Sans Nom';
                const isSuspended = item.suspended;
                const vehicleStr = item.vehicleBrand || item.vehicleModel 
                  ? `${item.vehicleBrand || ''} ${item.vehicleModel || ''}`.trim() 
                  : 'N/A';
                
                return (
                  <tr 
                    key={item.id} 
                    onClick={() => handleDriverSelect(item)} 
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: selectedDriver && selectedDriver.id === item.id ? 'rgba(0, 82, 255, 0.04)' : 'transparent' 
                    }}
                  >
                    <td>
                      <div style={styles.userCell}>
                        <div style={{
                          ...styles.avatar,
                          backgroundColor: isSuspended ? 'var(--danger-bg)' : 'rgba(0, 82, 255, 0.08)',
                          borderColor: isSuspended ? 'var(--danger-border)' : 'rgba(0, 82, 255, 0.15)'
                        }}>
                          {item.firstName ? item.firstName[0].toUpperCase() : <User size={14} />}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={styles.userName}>{fullName}</span>
                            {item.online ? (
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: 'var(--success)', 
                                display: 'inline-block' 
                              }} title="En ligne" />
                            ) : (
                              <span style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: 'var(--text-muted)', 
                                opacity: 0.5,
                                display: 'inline-block' 
                              }} title="Hors ligne" />
                            )}
                          </div>
                          <div style={styles.userId}>{item.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={styles.contactCell}>
                        <span style={styles.contactItem}><Mail size={12} /> {item.email}</span>
                        {item.phone && <span style={styles.contactItem}><Phone size={12} /> {item.phone}</span>}
                      </div>
                    </td>
                    <td>
                      <div style={styles.vehicleCell}>
                        <span style={{ fontSize: '0.85rem', fontWeight: '500', color: 'var(--text-primary)' }}>
                          <Car size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                          {vehicleStr}
                        </span>
                        {item.licensePlate && (
                          <span style={styles.plateTag}>{item.licensePlate}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={styles.ratingSolde}>
                        <span style={styles.soldeText}>{(item.balance !== undefined ? item.balance : 0).toFixed(2)} €</span>
                        <span style={styles.ratingText}>
                          <Star size={12} fill="#eab308" color="#eab308" /> 
                          {item.rating ? item.rating.toFixed(1) : '5.0'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${item.revenueMode === 'SUBSCRIPTION' ? 'info' : 'secondary'}`}>
                        {item.revenueMode === 'SUBSCRIPTION' ? 'Abonnement' : 'Commission'}
                      </span>
                    </td>
                    <td>
                      {isSuspended ? (
                        <span className="status-badge danger">Suspendu</span>
                      ) : (
                        <span className="status-badge success">Actif</span>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      <div style={styles.actionsCell}>
                        <button 
                          onClick={() => handleDriverSelect(item)} 
                          style={styles.actionIconBtn} 
                          title="Voir fiche 360°"
                        >
                          <Eye size={16} color="var(--primary)" />
                        </button>
                        <button 
                          onClick={() => handleToggleDriverSuspension(item)} 
                          style={{ 
                            ...styles.actionIconBtn, 
                            backgroundColor: isSuspended ? 'var(--success-bg)' : 'var(--danger-bg)' 
                          }} 
                          title={isSuspended ? "Réactiver" : "Suspendre"}
                        >
                          {isSuspended ? <UserCheck size={16} color="var(--success)" /> : <UserX size={16} color="var(--danger)" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const styles = {
  centerContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(0, 82, 255, 0.1)',
    borderTop: '3px solid var(--primary)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '0.85rem',
  },
  filterBar: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    padding: '4px',
  },
  searchIcon: {
    position: 'absolute',
    left: '16px',
    color: 'var(--text-muted)',
    pointerEvents: 'none',
  },
  searchInput: {
    paddingLeft: '40px',
  },
  tablePanel: {
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-main)',
  },
  emptyTable: {
    padding: '48px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontFamily: 'var(--font-title)',
  },
  userCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--primary)',
    flexShrink: 0,
  },
  userName: {
    fontWeight: '500',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
  },
  userId: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    fontFamily: 'monospace',
    marginTop: '2px',
  },
  contactCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  contactItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  vehicleCell: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  plateTag: {
    fontSize: '0.75rem',
    fontFamily: 'monospace',
    backgroundColor: 'var(--bg-tertiary)',
    padding: '2px 6px',
    borderRadius: '4px',
    width: 'fit-content',
    color: 'var(--text-secondary)',
  },
  ratingSolde: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  soldeText: {
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  ratingText: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '0.8rem',
    color: 'var(--text-secondary)',
  },
  actionsCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  actionIconBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: '0.5px solid var(--border-color)',
    backgroundColor: 'var(--bg-secondary)',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  profileSummaryCard: {
    padding: '24px',
    backgroundColor: 'var(--bg-secondary)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  profileSummaryHeader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '20px',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '20px',
    width: '100%',
  },
  detailLargeAvatar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    fontSize: '2rem',
    fontWeight: '600',
    color: 'var(--primary)',
    marginBottom: '16px',
  },
  detailProfileName: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  detailProfileRole: {
    fontSize: '0.85rem',
    color: 'var(--text-muted)',
    marginTop: '4px',
  },
  profileDetailsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '20px',
    marginBottom: '20px',
  },
  detailItemRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
    fontSize: '0.85rem',
    textAlign: 'left',
    width: '100%',
  },
  detailItemKey: {
    color: 'var(--text-secondary)',
    fontWeight: '500',
  },
  detailItemVal: {
    color: 'var(--text-primary)',
    wordBreak: 'break-all',
  },
  detailItemValMono: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: 'var(--text-primary)',
    wordBreak: 'break-all',
  },
  profileActionBlock: {
    width: '100%',
    textAlign: 'left',
  },
  actionBlockTitle: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '12px',
  },
  actionButtonsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  managerActionBtnPrimary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: 'rgba(0, 82, 255, 0.05)',
    color: 'var(--primary)',
    border: '0.5px solid rgba(0, 82, 255, 0.15)',
    transition: 'all 0.2s ease',
    width: '100%',
  },
  managerActionBtnDanger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
    borderWidth: '0.5px',
    borderStyle: 'solid',
    transition: 'all 0.2s ease',
    width: '100%',
  },
  detailTabLoadingContainer: {
    padding: '48px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bg-secondary)',
    height: '240px',
  },
  detailSectionCard: {
    backgroundColor: 'var(--bg-secondary)',
  },
  detailSectionHeader: {
    padding: '16px 20px',
    borderBottom: '0.5px solid var(--border-color)',
  },
  detailSectionTitle: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
  },
  detailSectionBody: {
    padding: '20px',
  },
  emptyDetailSection: {
    padding: '24px',
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '0.85rem',
  },
  docList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  docItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 14px',
    backgroundColor: 'var(--bg-primary)',
    border: '0.5px solid var(--border-color)',
    borderRadius: 'var(--radius-sm)',
  },
  docMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  docName: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  docUrl: {
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
  },
  docLinkButton: {
    fontSize: '0.8rem',
    color: 'var(--primary)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    padding: '2px 0',
    textAlign: 'left',
    textDecoration: 'underline',
  },
  statusBadgeSmall: {
    fontSize: '0.7rem',
    fontWeight: '600',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  editCardHeader: {
    borderBottom: '0.5px solid var(--border-color)',
    paddingBottom: '16px',
    marginBottom: '24px',
  },
  editForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  formAlertError: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    backgroundColor: 'var(--danger-bg)',
    border: '0.5px solid var(--danger-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--danger)',
    fontSize: '0.85rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '500',
    color: 'var(--text-secondary)',
  },
  formInput: {
    padding: '10px 14px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'var(--bg-primary)',
    borderRadius: 'var(--radius-sm)',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)',
  },
  modalCloseBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    padding: '0 8px',
  },
  documentFrameContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  documentFrame: {
    width: '100%',
    height: '100%',
    border: 'none',
  }
};

